// frontend/src/pages/reporting/reporting/ProfilePractitionerTab.jsx
/*
 * Single-practitioner readout from clinic report "Profile" tab.
 * Practitioner Lead: hideFinancialFields=true — show only personal/basic (name, contact, type, status, corp, provider).
 * Everyone else: full row list including compensation / insurance / grid / base pay.
 */
import { Card, Button } from "@common";
import { safeText } from "@utils/utils";
import {
  parseOtherProfit,
  parseOtherDeduction,
  normalizeOtherDeductionMode,
  OTHER_DEDUCTION_MODE_AMOUNT,
} from "@utils/practitionerFormUtils";

function fmtPct(v) {
  const s = safeText(v).trim();
  if (s === "") return "";
  return `${s}%`;
}

function pickAllBasePay(practitioner) {
  const arr = practitioner?.base_pay;
  if (Array.isArray(arr) && arr.length > 0) {
    return arr
      .filter((r) => r && (r.base_pay !== undefined || r.pay_percent !== undefined))
      .map((r) => ({
        basePay: r.base_pay ?? "",
        basePayPct: r.pay_percent ?? "",
      }));
  }
  return [];
}

function yesNo(v) {
  return v ? "Yes" : "No";
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-2 gap-4 py-2 border-b border-palette-gray-subtle">
      <div className="text-sm text-palette-gray-secondary">{label}</div>
      <div className="text-sm font-semibold text-palette-slate-primary text-right">
        {safeText(value) || "—"}
      </div>
    </div>
  );
}

export default function ProfilePractitionerTab({
  practitioner,
  onBackToProfile,
  hideFinancialFields = false, // ClinicReport sets true when isPractitionerLead(user)
}) {
  if (!practitioner) {
    return (
      <div className="mx-auto w-full max-w-5xl px-4 mt-6">
        <Card className="p-6">
          <div className="text-sm text-palette-gray-badge">
            No profile selected. Click <b>View</b> on a practitioner to open this tab.
          </div>
          {onBackToProfile && (
            <div className="mt-4">
              <Button variant="outline" onClick={onBackToProfile}>
                Back to Profile
              </Button>
            </div>
          )}
        </Card>
      </div>
    );
  }

  const basePayRows = pickAllBasePay(practitioner);
  // When false for PL: skip entire block below (fees, insurance, grid, base pay tiers)
  const showFinancial = !hideFinancialFields;

  // other_profit / other_deduction are stored on the Practitioner row as JSON strings
  // of tiers [{ label, percent }]. Parse into rows for display.
  const otherProfitRows = parseOtherProfit(practitioner?.other_profit).rows;
  const otherDeductionRows = parseOtherDeduction(practitioner?.other_deduction).rows;

  return (
    <div className="mx-auto w-full max-w-5xl px-4 mt-6">
      <Card className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-lg font-bold text-palette-slate-primary">
              {safeText(practitioner?.name) || "Profile"}
            </div>
            <div className="mt-1 text-xs text-palette-gray-secondary">
              ID: {safeText(practitioner?.id) || "—"}
            </div>
          </div>

          {onBackToProfile && (
            <Button variant="outline" onClick={onBackToProfile}>
              Back to Profile
            </Button>
          )}
        </div>

        <div className="mt-6">
          {/* --- Rows below: allowed for Practitioner Lead (personal / basic only) --- */}
          <Row label="Name" value={practitioner?.name} />
          <Row
            label="Type"
            value={practitioner?.practitioner_type_display ?? practitioner?.practitioner_type}
          />
          <Row label="Status" value={practitioner?.status} />
          <Row label="Corporation Name" value={practitioner?.actual_name} />
          <Row label="Email" value={practitioner?.email} />
          <Row label="Provider Code" value={practitioner?.provider_code} />
          {/* Identity/employment fields: visible to all roles including Practice Lead */}
          <Row label="Position ID" value={practitioner?.position_id} />
          <Row label="Associate ID" value={practitioner?.associate_id} />
          <Row label="Rate Type" value={practitioner?.rate_type} />
          <Row label="Hire Date" value={practitioner?.hire_date} />
          <Row label="Supervisor" value={practitioner?.supervisor} />
          {/* --- Rows below: hidden for PL (compensation / insurance / fee grid / payment-related) --- */}
          {showFinancial && (
            <>
              <Row label="Paid Based On" value={practitioner?.paid_based_on} />
              <Row label="Associate Fee (%)" value={practitioner?.associate_fee_percentage} />
              <Row label="Per day" value={yesNo(!!practitioner?.per_day)} />
              {!!practitioner?.per_day && (
                <Row label="Per Day Value" value={practitioner?.per_day_value} />
              )}
              <Row label="Insurance" value={practitioner?.insurance} />
              <Row label="Grid" value={yesNo(!!practitioner?.grid)} />
              <Row label="Include HYG" value={yesNo(!!practitioner?.include_hyg)} />

              {otherProfitRows.map((row, idx) => (
                <Row
                  key={`op-${idx}`}
                  label={`Other Profit${row.label ? ` — ${row.label}` : ""}`}
                  value={fmtPct(row.percent)}
                />
              ))}
              {otherDeductionRows.map((row, idx) => (
                <Row
                  key={`od-${idx}`}
                  label={`Other Deduction${row.label ? ` — ${row.label}` : ""}`}
                  // Deduction tiers hold an $/hr rate or nothing at all (a
                  // fixed-amount tier is entered on the monthly report), never
                  // the percentage this used to print.
                  value={
                    normalizeOtherDeductionMode(row.mode) === OTHER_DEDUCTION_MODE_AMOUNT
                      ? "Amount entered monthly"
                      : row.hour_rate === "" || row.hour_rate === undefined
                        ? "—"
                        : `$${safeText(row.hour_rate)}/hr`
                  }
                />
              ))}

              {basePayRows.length > 0 ? (
                basePayRows.map((row, idx) => (
                  <div key={idx}>
                    <Row
                      label={`Base Pay ${basePayRows.length > 1 ? idx + 1 : ""}`}
                      value={safeText(row.basePay)}
                    />
                    <Row
                      label={`Base Pay ${basePayRows.length > 1 ? idx + 1 : ""} %`}
                      value={safeText(row.basePayPct)}
                    />
                  </div>
                ))
              ) : (
                <>
                  <Row label="Base Pay" value="" />
                  <Row label="Base Pay (%)" value="" />
                </>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
